import { Buttons, CancelButton, Drawer, MultilineText, Stack, Tab, Tabs, TaskCategoryIcon } from '~/components';
import ReadForm from '~/components/read-only/ReadForm';
import ReadTextbox from '~/components/read-only/ReadTextbox';
import { useDocumentTitle } from '~/hooks';
import projectTaskStatuses from '~/lookups/project-task-statuses';
import moment from 'moment';
import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '~/styles';
import { dateFormats } from '~/utils';
import ProjectTaskHistory from './ProjectTaskHistory';

const TagContainer = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding-left: 0.625em;
  padding-right: 2.5em;
  max-width: 100%;
  background-color: ${colors.grey5};
  border: solid 1px ${colors.grey10};
  border-radius: 0.3125rem;
`;

const TagItem = styled.div`
  border-radius: 0.3125rem;
  background-color: ${colors.grey10};
  padding: 0.25rem 0.5rem;
  display: flex;
  align-items: center;
  cursor: default;
  margin: 0.5rem 0.25rem 0.25rem 1rem;
`;

const TagPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0.625rem;
  margin-left: 1px;
  padding: 0 0.25rem;
  color: ${colors.grey40};
  font-size: 0.75rem;
  background-color: ${colors.white};
  border-radius: 0.3125rem;
  transform: translateY(-50%);
  transition: opacity 100ms;
  pointer-events: none;
`;

const TaskName = styled.span`
  display: flex;
  align-items: center;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: 1.625rem;
  margin-bottom: 1.625rem;
`;

const Control = ({ visible = true, children }) => (visible ? <ReadForm.Control>{children}</ReadForm.Control> : null);

const Description = ({ visible = true, label, children }) =>
  visible ? <ReadTextbox label={label} value={children} /> : null;

function ProjectTaskMilestoneDetails({ task, project, onClose }) {
  const [tabIndex, setTabIndex] = useState(0);

  useDocumentTitle('Task Details');

  return (
    <Drawer title="Task Details" isOpen onClose={onClose}>
      {(closeDrawer) => (
        <Stack>
          <Tabs selectedIndex={tabIndex} onChange={(index) => setTabIndex(index)}>
            <Tab>Task</Tab>
            {task.permissions.manage && <Tab>History</Tab>}
          </Tabs>

          <Content>
            {
              [
                <React.Fragment key="task">
                  <ReadForm.Section title="Basic Information">
                    <Control>
                      <Description label="Task Name">
                        <TaskName>
                          <TaskCategoryIcon icon={task.taskCategory.icon} color={task.taskCategory.color} />
                          {task.name}
                        </TaskName>
                      </Description>
                    </Control>

                    <Control>
                      <Description label="Status">{projectTaskStatuses[task.statusId]?.name}</Description>
                    </Control>

                    <Control visible={!!task.start}>
                      <Description label="Date">
                        {task.start && moment(task.start).format(dateFormats.compactDate)}
                      </Description>
                    </Control>

                    <Control visible={!!task.description}>
                      <Description label="Notes">
                        <MultilineText value={task.description} />
                      </Description>
                    </Control>

                    <Control visible={!!task.parent}>
                      <Description label="Parent Task">{task.parent?.name}</Description>
                    </Control>

                    <Control visible={!!task.code}>
                      <Description label="Task Code">{task.code}</Description>
                    </Control>

                    <Control visible={task.tags.length > 0}>
                      <TagContainer>
                        {task.tags.map((tag) => (
                          <TagItem key={tag.id}>{tag.name}</TagItem>
                        ))}
                        <TagPlaceholder>Tags</TagPlaceholder>
                      </TagContainer>
                    </Control>
                  </ReadForm.Section>
                </React.Fragment>,
                <ProjectTaskHistory key="history" projectId={project.id} taskId={task.id} />,
              ][tabIndex]
            }
          </Content>

          <Drawer.Actions>
            <Buttons align="right">
              <CancelButton onClick={closeDrawer}>Close</CancelButton>
            </Buttons>
          </Drawer.Actions>
        </Stack>
      )}
    </Drawer>
  );
}

export default ProjectTaskMilestoneDetails;
